import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { IPSearchAnalyticsService } from "@analytics/index";

import { clearSearch, companySearch, companySearchSSE, getInsight, loadCompanySearch } from "@api/ipSearch.api";

import C from "@constants/constants";

import { GetChatRequestType } from "@interfaces/chat.type";
import { SearchType } from "@interfaces/ipsearch.type";

import { PopupActions } from "@store/popupStore";
import { UserActions } from "@store/userStore";

export const useLoadCompanySearch = (searchId: string) => {
  return useQuery<SearchType, Error>({
    enabled: searchId !== "new",
    queryFn: async () => {
      return await loadCompanySearch(searchId);
    },
    queryKey: ["search", searchId],
    staleTime: C.STALE_TIME,
  });
};

export const useCompanySearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      preferredIndustries,
      queryString,
    }: {
      preferredIndustries?: string[];
      queryString: string;
    }) => {
      IPSearchAnalyticsService.trackIpSearchInitiated(queryString);
      return await companySearch(queryString, preferredIndustries);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["search", data.id], data);
      UserActions.setCurrentSearch(data.id ?? "new");
    },
  });
};

export const useFetchCompanyInsight = () => {
  return useMutation<
    SearchType,
    Error,
    { companyId: string; messages?: string[]; model: GetChatRequestType["model"]; searchId: string }
  >({
    mutationFn: async ({ companyId, messages, model, searchId }) => {
      return await getInsight(searchId, companyId, model, messages);
    },
    onSuccess: () => {
      IPSearchAnalyticsService.fetchAIInsights();
    },
  });
};

export const useClearSearch = () => {
  return useMutation<void, Error>({
    mutationFn: async () => await clearSearch(),
    onError: () => {
      PopupActions.openNotification({
        content: "There was an issue clearing your Smart Search. Please try again.",
        title: "Error",
      });
    },
    onSuccess: () => {
      PopupActions.openAlert({
        duration: 4000,
        message: "Smart Search successfully cleared.",
        severity: "success",
      });
    },
  });
};

// SSE version for long-running searches that avoids Cloudflare timeout
export const useCompanySearchSSE = () => {
  const queryClient = useQueryClient();
  const [progressMessage, setProgressMessage] = useState<string>("");

  return {
    mutation: useMutation({
      mutationFn: async ({
        preferredIndustries,
        queryString,
      }: {
        preferredIndustries?: string[];
        queryString: string;
      }) => {
        IPSearchAnalyticsService.trackIpSearchInitiated(queryString);

        // Use SSE version with progress callback
        return await companySearchSSE(queryString, preferredIndustries, (message) => setProgressMessage(message));
      },
      onError: () => {
        setProgressMessage("");
        PopupActions.openNotification({
          content: "Search failed. Please try again.",
          title: "Error",
        });
      },
      onSuccess: (data) => {
        queryClient.setQueryData(["search", data.id], data);
        UserActions.setCurrentSearch(data.id ?? "new");
        setProgressMessage("");
      },
    }),
    progressMessage,
  };
};
